import {API_BASE_URLS, PROJECT_IDS} from 'common/apis'
import {Environment} from 'common/enums'

/**
 * Render-only helper. Admin transport payloads and editor form state keep their
 * canonical storage references so an edit, save or delete still resolves
 * against the backend storage parsers.
 */
const PUBLIC_MEDIA_DELIVERY_PATH = '/public-media'
const LOCAL_API_BASE_URL = 'http://127.0.0.1:5100'

const resolveEnvironment = () =>
  (process.env.REACT_APP_PUBLIC_APPLICATION_ENVIRONMENT as Environment) ||
  Environment.DEVELOPMENT

const trimTrailingSlash = (value: string) => value.replace(/\/+$/, '')

const normalizeBucketName = (value?: string | null) =>
  `${value ?? ''}`
    .trim()
    .replace(/^gs:\/\//i, '')
    .replace(/\/.*$/, '')
    .toLowerCase()

const dadeProjectIds = Object.values(PROJECT_IDS).filter(Boolean)

/**
 * The one public-media bucket this deployment's delivery API actually reads.
 * Another Dade environment's bucket holds different objects at the same paths.
 */
export const resolveActivePublicMediaBucket = () => {
  const configured = normalizeBucketName(
    process.env.REACT_APP_PUBLIC_MEDIA_STORAGE_BUCKET
  )
  if (configured) return configured

  const projectId = PROJECT_IDS[resolveEnvironment()]
  return projectId ? normalizeBucketName(`${projectId}-public-media`) : ''
}

const TRUSTED_STORAGE_BUCKETS = new Set(
  [
    process.env.REACT_APP_PUBLIC_MEDIA_STORAGE_BUCKET,
    ...dadeProjectIds.map((projectId) => `${projectId}-public-media`),
    process.env.REACT_APP_PUBLIC_DEVELOPMENT_STORAGE_BUCKET,
    process.env.REACT_APP_PUBLIC_STAGING_STORAGE_BUCKET,
    process.env.REACT_APP_PUBLIC_PRODUCTION_STORAGE_BUCKET,
    ...dadeProjectIds.flatMap((projectId) => [
      `${projectId}.appspot.com`,
      `${projectId}.firebasestorage.app`
    ])
  ]
    .map(normalizeBucketName)
    .filter(Boolean)
)

const PUBLIC_MEDIA_PREFIXES = /^(?:categories|content-pages|assets)\//
const PRODUCT_MEDIA_PATH =
  /^products\/[A-Za-z0-9_-]{1,160}\/(?:images|videos|imageThumbnails|videoThumbnails)\/[^/]+$/
const PRODUCT_METADATA_PATH =
  /^products\/([A-Za-z0-9_-]{1,160})\/(?:images|videos|attachments|documents|files|pdfs)\/[^/]+$/
const ADMIN_ASSET_METADATA_PATH = /^(?:categories|content-pages|assets)\/[^/]+/

const hasSafeObjectPathSegments = (objectPath: string) =>
  Boolean(objectPath) &&
  objectPath.length <= 1024 &&
  !objectPath.startsWith('/') &&
  !objectPath.includes('\\') &&
  objectPath
    .split('/')
    .every((segment) => segment && segment !== '.' && segment !== '..')

export const isPublicMediaObjectPath = (objectPath: string) => {
  if (!hasSafeObjectPathSegments(objectPath)) return false

  return (
    PUBLIC_MEDIA_PREFIXES.test(objectPath) ||
    PRODUCT_MEDIA_PATH.test(objectPath) ||
    (!objectPath.includes('/') && /placeholder/i.test(objectPath))
  )
}

export const resolvePublicMediaBaseUrl = () => {
  const configured = [
    process.env.REACT_APP_API_URL,
    API_BASE_URLS[resolveEnvironment()]
  ]
    .map((value) => trimTrailingSlash(`${value ?? ''}`.trim()))
    .find((value) => {
      if (!value) return false
      try {
        const {protocol} = new URL(value)
        return protocol === 'https:' || protocol === 'http:'
      } catch {
        return false
      }
    })

  if (configured) return configured

  return resolveEnvironment() === Environment.DEVELOPMENT
    ? LOCAL_API_BASE_URL
    : ''
}

export const publicMediaDeliveryUrl = (objectPath: string) => {
  const apiBaseUrl = resolvePublicMediaBaseUrl()
  if (!apiBaseUrl) return ''

  const encodedObjectPath = objectPath
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')

  return `${apiBaseUrl}${PUBLIC_MEDIA_DELIVERY_PATH}/${encodedObjectPath}`
}

export const isPublicMediaDeliveryUrl = (url: string) => {
  const apiBaseUrl = resolvePublicMediaBaseUrl()
  if (!apiBaseUrl) return false

  try {
    const parsed = new URL(url)
    const deliveryRoot = new URL(`${apiBaseUrl}${PUBLIC_MEDIA_DELIVERY_PATH}/`)
    return (
      parsed.origin === deliveryRoot.origin &&
      parsed.pathname.startsWith(deliveryRoot.pathname)
    )
  } catch {
    return false
  }
}

const parseTrustedPublicMediaReference = (value: string) => {
  let parsedUrl: URL
  try {
    parsedUrl = new URL(value)
  } catch {
    return null
  }

  // A malformed percent sequence must never throw while a page is rendering.
  const decode = (segment: string) => {
    try {
      return decodeURIComponent(segment)
    } catch {
      return null
    }
  }

  let bucket = ''
  let objectPath = ''
  if (parsedUrl.hostname === 'storage.googleapis.com') {
    const segments = parsedUrl.pathname.split('/').filter(Boolean)
    const decodedBucket = decode(segments.shift() || '')
    const decodedPath = decode(segments.join('/'))
    if (decodedBucket === null || decodedPath === null) return null
    bucket = decodedBucket
    objectPath = decodedPath
  } else if (parsedUrl.hostname === 'firebasestorage.googleapis.com') {
    const match = parsedUrl.pathname.match(/^\/v0\/b\/([^/]+)\/o\/(.+)$/)
    if (!match) return null
    const decodedBucket = decode(match[1] || '')
    const decodedPath = decode(match[2] || '')
    if (decodedBucket === null || decodedPath === null) return null
    bucket = decodedBucket
    objectPath = decodedPath
  } else {
    return null
  }

  if (!bucket || !objectPath) return null
  if (parsedUrl.search) return null
  const activeBucket = resolveActivePublicMediaBucket()
  if (!activeBucket || bucket.toLowerCase() !== activeBucket) return null
  if (!TRUSTED_STORAGE_BUCKETS.has(bucket.toLowerCase())) return null

  return objectPath
}

export type StorageMetadataTarget = {
  objectPath: string
  productId?: string
}

/**
 * Resolves a stored public-media reference to the relative path expected by
 * the authenticated metadata API. Relative references remain supported for
 * existing editors, while absolute references must belong to this
 * environment's active public bucket.
 */
export const resolveStorageMetadataTarget = (
  reference?: string | null
): StorageMetadataTarget | null => {
  if (typeof reference !== 'string' || !reference) return null

  let objectPath = reference
  const isAbsoluteReference = /^https?:\/\//i.test(reference)
  if (isAbsoluteReference) {
    try {
      const parsed = new URL(reference)
      if (
        parsed.protocol !== 'https:' ||
        parsed.username ||
        parsed.password ||
        parsed.port ||
        parsed.search ||
        parsed.hash
      ) {
        return null
      }

      // URL parsing removes dot segments. Inspect the encoded input first so
      // a traversal-looking reference cannot be normalized into an allowed
      // metadata path.
      const rawPath = reference.match(/^https:\/\/[^/?#]+([^?#]*)/i)?.[1] || ''
      const decodedRawPath = decodeURIComponent(rawPath)
      const hasControlCharacter = decodedRawPath.split('').some((character) => {
        const code = character.charCodeAt(0)
        return code <= 31 || code === 127
      })
      if (
        hasControlCharacter ||
        decodedRawPath.includes('\\') ||
        decodedRawPath
          .slice(1)
          .split('/')
          .some((segment) => !segment) ||
        decodedRawPath
          .split('/')
          .some((segment) => segment === '.' || segment === '..')
      ) {
        return null
      }
    } catch {
      return null
    }
    objectPath = parseTrustedPublicMediaReference(reference) || ''
  }

  if (!hasSafeObjectPathSegments(objectPath)) return null
  if (isAbsoluteReference && !isPublicMediaObjectPath(objectPath)) return null

  const productMatch = objectPath.match(PRODUCT_METADATA_PATH)
  if (productMatch?.[1]) {
    return {objectPath, productId: productMatch[1]}
  }

  return ADMIN_ASSET_METADATA_PATH.test(objectPath) ? {objectPath} : null
}

/**
 * Renders a stored media reference. Only a trusted Dade public-media reference
 * becomes a delivery URL; a foreign bucket, a signed or tokenized URL, a
 * private reference and any non-storage value are returned unchanged.
 */
export const normalizePublicStorageUrl = (url?: string | null) => {
  if (typeof url !== 'string' || !url) return url ?? ''

  const objectPath = parseTrustedPublicMediaReference(url)
  if (!objectPath || !isPublicMediaObjectPath(objectPath)) return url

  return publicMediaDeliveryUrl(objectPath) || url
}
